import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSections } from "../../hooks/use-sections";
import Button from "../../components/button";
import PageContainer from "../../components/page-container";
import PageHeader from "../../components/page-header";
import { ReactComponent as ArrowsRight } from "../../assets/arrowsright.svg";
import { ReactComponent as ArrowsLeft } from "../../assets/arrowsleft.svg";
import s from "./styles.module.scss";

const EventsAdded = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { calendar, webcalUrl, totalEvents } = state || {};
  const { selectedSections } = useSections();
  const [copySuccess, setCopySuccess] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Calendar name mapping
  const calendarNames = {
    google: "Google Calendar",
    apple: "Apple Calendar",
    outlook: "Outlook Calendar",
  };

  const eventsCount = totalEvents || Object.keys(selectedSections).length;
  const calendarName = calendarNames[calendar] || "Calendar";

  const handleAppleCalendarSubscribe = () => {
    if (!webcalUrl){
      console.error("No webcal URL available for Apple Calendar subscription.");
      return;
    }

    // Ccnvert webcal URL to webcal protocol for calendar app launch
    const webcalProtocolURL = webcalUrl.replace("http://", "webcal://").replace("https://", "webcal://");

    try {

      // directly open the webcal URL in a new tab
      window.location.href = webcalProtocolURL;

      // copy webcal URL to clipboard as a backup
      navigator.clipboard.writeText(webcalUrl);

      // success message
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 3000);
    } catch (error) {
      alert("Failed to open Apple Calendar subscription link. Please copy the link manually.");
    }
  };

  const handleGoToCalendar = () => {
    navigate("/calendar");
  };

  const handleGoToCourses = () => {
    navigate("/courses");
  };

  const handleCopyLink = async () => {
    if (!webcalUrl) return;

    try {
      await navigator.clipboard.writeText(webcalUrl);
      setCopySuccess(true);

      // Reset the success message after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = webcalUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    }
  };

  // Function to open the help text
  const handleShowHelp = () => {
    setShowHelp(true);
  }

  // Function to close the help text
  const handleCloseHelp = () => {
    setShowHelp(false);
  }

  // Function to create apple help text
  const getHelpText = () => {
    if (calendar === "apple") {
      return {
        title: "Apple Calendar didn't open?",
        subtitle: "Here's how to add your events:",
        steps: [
          "1. Click \"Open Apple Calendar\" and the webcal link will be copied to your clipboard",
          "2. Open the Apple Calendar app on your device",
          "3. Paste the webcal link into your Apple Calendar:",
          "\u00A0\u00A0\u00A0a. Mac: Open the \"File\" tab on the top of your screen, then select \"New Calendar Subscription\"",
          "\u00A0\u00A0\u00A0b. iPhone/iPad: Open \"Calendars\", then \"Add Calendar\" in the bottom left of your screen and select \"Add Subscription Calendar\""
        ]
      };
    }
    return null;
  }

  const helpText = getHelpText();

  return (
    <PageContainer>
      <PageHeader
        title={calendar === "apple" ? `${eventsCount} events to add to your Apple Calendar` : `${eventsCount} events added to your ${calendarName}`}
        description={calendar === "apple" ? `Click the button to open Apple Calendar and add events!` : `Your events are now in ${calendarName}. You're all set!`}
      />

      <div className={s.actionsContainer}>
        {webcalUrl && (
          <div className={s.appleButtonRow}>
          <Button
            variant="secondary"
            onClick={calendar === "apple" ? handleAppleCalendarSubscribe : handleCopyLink}
            className={s.copyLinkButton}
          >
            {copySuccess ? "Apple Calendar opened and link copied!" : "Open Apple Calendar"}
          </Button>
          </div>
        )}

        <div className={s.navigationButtonsRow}>
        <Button
          variant="primary"
          onClick={handleGoToCalendar}
          className={s.homepageButton}
        >
          <span className={s.buttonArrowLeft}><ArrowsLeft /></span>
          <span className={s.buttonTextContent}>Calendar</span>
        </Button>


        <Button
          variant="primary"
          onClick={handleGoToCourses}
          className={s.coursesButton}
        >
          <span className={s.buttonTextContent}>Course dashboard</span>
          <span className={s.buttonArrowRight}><ArrowsRight /></span>
        </Button>
        </div>

        {calendar === "apple" && (
          <button
            onClick={handleShowHelp}
            className={s.helpLink}
            type="button"
          >
            Apple Calendar didn't open?
          </button>
        )}
      </div>

      {showHelp && helpText && (
        <div className={s.modalOverlay} onClick={handleCloseHelp}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={s.closeButton} onClick={handleCloseHelp}>
              ×
            </button>
            <h3 className={s.modalTitle}>{helpText.title}</h3>
            <p className={s.modalSubtitle}>{helpText.subtitle}</p>
            <div className={s.modalSteps}>
              {helpText.steps.map((step, index) => (
                <p key={index} className={s.modalStep}>{step}</p>
              ))}
            </div>
          </div>
        </div>
      )}    
    </PageContainer>
  );
};

export default EventsAdded;
